import React, { useEffect, useRef, useState } from 'react';
import { TranslationView } from '@levelapp/softfabric-ui';
import { findNodeHandle, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { styles } from './NavigationBarViewStyle';
import { useWindowSizeContext } from "../../../../../Common/contexts/WindowSizeContext";

interface NavigationBarViewProps {
    sectionsShownedNames: string[];
}

export const NavigationBarView = ({ sectionsShownedNames }: NavigationBarViewProps) => {
    const { isMobile } = useWindowSizeContext();
    const [selectedSectionIndex, setSelectedSectionIndex] = useState<number>(0);
    const isScrollingProgrammatically = useRef(false);

    const scrollViewRef = useRef<ScrollView>(null);

    const itemRefs = useRef<(TouchableOpacity | null)[]>([]); // 2. Refs pour les éléments

    const [showRightArrow, setShowRightArrow] = useState(false);
    const [currentScrollX, setCurrentScrollX] = useState(0);

    function calculateThreshold(lastIndex: number, newIndex: number) {
        return newIndex < lastIndex ? -175 : 15;
    }

    const scrollToHorizontalItem = (index: number) => {
        const item = itemRefs.current[index];
        const scrollViewNode = scrollViewRef.current && findNodeHandle(scrollViewRef.current);

        if (item && scrollViewNode) {
            item.measureLayout(
                scrollViewNode,
                (x) => {
                    scrollViewRef.current?.scrollTo({ x: x - 10, animated: true });
                },
                () => { }
            );
        }
    };

    const onNavSectionClick = (sectionName: string, index: number) => {
        const sectionDimensions = getElementDimensions(sectionName);
        const threshold = calculateThreshold(selectedSectionIndex, index);

        if (sectionDimensions) {
            const elementTop: number = sectionDimensions.top + window.scrollY + threshold;

            isScrollingProgrammatically.current = true;
            window.scrollTo({
                top: elementTop,
                behavior: "smooth",
            });

            setTimeout(() => {
                isScrollingProgrammatically.current = false;
            }, 700);
        }

        setSelectedSectionIndex(index);
        scrollToHorizontalItem(index); // 🔥 scroll horizontal
    };

    const getElementDimensions = (elementId: string) => {
        const element = document.getElementById(elementId);
        if (!element) return null;
        return element.getBoundingClientRect();
    };

    function handleScroll(event: any) {
        if (isScrollingProgrammatically.current) return;
        if (event && event.nativeEvent) {
            const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent;
            const isEndReached = contentOffset.x + layoutMeasurement.width >= contentSize.width - 10;

            setShowRightArrow(!isEndReached);

            // 🔥 Sauvegarde la position actuelle
            setCurrentScrollX(contentOffset.x);
        }

        setSelectedSectionIndex((prevIndex) => {
            const nextSectionDimensions = getElementDimensions(sectionsShownedNames[prevIndex + 1]);
            const lastSectionDimensions = getElementDimensions(sectionsShownedNames[prevIndex - 1]);
            const scrollY = window.scrollY;

            if (nextSectionDimensions && scrollY > nextSectionDimensions.y + scrollY - Math.abs(calculateThreshold(prevIndex, prevIndex + 1))) {
                const nextIndex = prevIndex + 1;
                scrollToHorizontalItem(nextIndex);
                return nextIndex;
            }

            if (lastSectionDimensions && scrollY < lastSectionDimensions.y + scrollY - Math.abs(calculateThreshold(prevIndex, prevIndex - 1))) {
                const prev = prevIndex - 1;
                scrollToHorizontalItem(prev);
                return prev;
            }

            return prevIndex;
        });
    }


    const handleRightArrowClick = () => {
        if (scrollViewRef.current) {
            scrollViewRef.current.scrollTo({
                x: currentScrollX + 100, // avance de 100px (ou ce que tu veux)
                animated: true,
            });
        }
    };

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        let width = window.innerWidth;
        if (width < 1725)
            setShowRightArrow(true);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [sectionsShownedNames]);

    return (
        <View style={{ position: 'relative' }}>
            <ScrollView
                ref={scrollViewRef}
                style={[styles.page, !isMobile && { width: "fit-content" }]}
                horizontal={true}
                showsHorizontalScrollIndicator={false}
                onScroll={handleScroll}
                scrollEventThrottle={16}
            >
                {sectionsShownedNames.map((sectionName, index) => {
                    const isSelected = sectionsShownedNames[selectedSectionIndex] === sectionName;
                    return (
                        <TouchableOpacity
                            key={index}
                            ref={(ref) => (itemRefs.current[index] = ref)}
                            style={[
                                styles.menu,
                                isSelected ? styles.selected : styles.unselected,
                                index !== sectionsShownedNames.length - 1 && { marginRight: 6 }
                            ]}
                            onPress={() => onNavSectionClick(sectionName, index)}
                        >
                            <TranslationView
                                style={[
                                    styles.navigationText,
                                    isSelected ? styles.navigationTextColorSelected : styles.navigationTextColorUnselected
                                ]}
                            >
                                {sectionName}
                            </TranslationView>
                        </TouchableOpacity>
                    );
                })}
            </ScrollView>

            {showRightArrow && (
                <TouchableOpacity
                    onPress={handleRightArrowClick}
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        justifyContent: 'center',
                        backgroundColor: 'rgb(83, 83, 83)', // pour bien voir la flèche
                        paddingHorizontal: 8,
                        borderRadius: 12,
                        shadowColor: "#0000006b",
                    }}
                >
                    <Text style={{ fontSize: 18, color: 'white' }}>{'>'}</Text>
                </TouchableOpacity>
            )}
        </View>
    );

}

export default NavigationBarView;